import { Card, CardBody } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { VideoInsight } from "@/api/studio/insights/types/insight.type";
import { BenchmarkComparison } from "@/api/studio/library/types/benchmark.type";
import { getVideoThumbnailUrl } from "@/api/renderer/api";
import { VideoMetricRow } from "@/features/reports/components/video-metric-row";
import { VideoBenchmarkPill } from "@/features/reports/components/video-benchmark-pill";

/* Prop Types */
interface VideoReportCardProps {
  insight: VideoInsight;
  isTopScore: boolean;
  projectId: string;
  studyId: string;
  benchmarkComparison?: BenchmarkComparison;
  onClick?: () => void;
}

/**
 * VideoReportCard component
 *
 * Renders a single video's report card: thumbnail, asset title, the Attention score as a
 * progress bar + percentage, an optional benchmark comparison pill, and the
 * Hook / Visual ease / Hold sub-metrics as coloured band rows.
 */
export const VideoReportCard = ({ insight, isTopScore, projectId, studyId, benchmarkComparison, onClick }: VideoReportCardProps) => {
  /* Consts */
  const { attention } = insight.creative;

  /* Render */
  return (
    <Card imageUrl={getVideoThumbnailUrl({ projectId, studyId, assetKey: insight.key })} imgFit="cover" winner={isTopScore} hideWinnerBorder size="xlarge" onClick={onClick}>
      <CardBody className="flex flex-col gap-4 px-6 py-5">
        <p className="font-semibold truncate text-sm text-base-content/60 pb-2">{insight.assetName}</p>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between text-base font-medium">
            <h3>Attention</h3>
            <div className="flex items-center gap-2">
              <Progress value={Math.round(attention.score)} className="w-[69px] h-1.5" />
              <h2>{Math.round(attention.score)}%</h2>
            </div>
          </div>

          {benchmarkComparison && (
            <div className="flex justify-end">
              <VideoBenchmarkPill comparison={benchmarkComparison} />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <VideoMetricRow label="Hook" score={attention.hook.score} />
          <VideoMetricRow label="Visual ease" score={attention.visualEase.score} />
          <VideoMetricRow label="Hold" score={attention.hold.score} />
        </div>
      </CardBody>
    </Card>
  );
};
